import type { ReactNode } from "react";
import { animated, config, useTransition } from "@react-spring/web";

import { useRequiredContext } from "@lib/utils";

import { PanelStackContext } from "../PanelStackContext";

const IN_VIEW = "translate3d(0%, 0, 0)";
const OFF_LEFT = "translate3d(-100%, 0, 0)";
const OFF_RIGHT = "translate3d(100%, 0, 0)";

// Rendered while it's the current page, and while it slides away, inert. The first page is there from the start.
export const PanelStackPage = ({ pageId, children }: { pageId: string; children: ReactNode }) => {
  const { currentPageId, lastMove } = useRequiredContext(PanelStackContext);
  const isPushing = lastMove === "push";
  const isCurrent = currentPageId === pageId;

  const transitions = useTransition(isCurrent, {
    initial: { transform: IN_VIEW },
    from: { transform: isPushing ? OFF_RIGHT : OFF_LEFT },
    enter: { transform: IN_VIEW },
    leave: { transform: isPushing ? OFF_LEFT : OFF_RIGHT },
    config: { ...config.stiff, clamp: true },
  });

  // A page that's still rendered but no longer current is sliding away
  return transitions(
    (style, isShown) =>
      isShown && (
        <animated.div
          style={style}
          aria-hidden={!isCurrent || undefined}
          inert={!isCurrent}
          className="flex min-h-0 flex-col bg-white [grid-area:1/1]">
          {children}
        </animated.div>
      ),
  );
};
